/** Measuring units */
type MeasuringUnit =
    | "eighth-point"
    | "emu"
    | "half-point"
    | "pt"
    | "px"
    | "twip";

/**
 * Applies HTML special chars
 *
 * @param content - Text content
 * @returns HTML after applying special chars
 */
const applyHtmlSpecialChars = (content: string): string => {
    return content
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
};

/**
 * Converts measuring units
 *
 * @param value - Value to be converted
 * @param from - Unit from
 * @param to - Unit to
 * @param round - Round conversion. Default as true
 * @returns Transformed value
 */
const convertMeasuringUnit = (
    value: number,
    from: MeasuringUnit,
    to: MeasuringUnit,
    round: boolean = true
): number | null => {
    // transform to pt all units
    switch (from) {
        case "eighth-point":
            value /= 8;
            break;
        case "emu":
            value /= 12700;
            break;
        case "half-point":
            value /= 2;
            break;
        case "px":
            value *= 0.75;
            break;
        case "twip":
            value /= 20;
            break;
        default:
            // pt and other not supported units
            break;
    }

    // transform pt to the new unit
    let newValue = null;
    switch (to) {
        case "eighth-point":
            newValue = value * 8;
            break;
        case "emu":
            newValue = value * 12700;
            break;
        case "half-point":
            newValue = value * 2;
            break;
        case "px":
            newValue = value / 0.75;
            break;
        case "twip":
            newValue = value * 20;
            break;
        default:
            // pt and other not supported units
            newValue = value;
            break;
    }

    return round ? Math.round(newValue) : newValue;
};

/**
 * Escapes a regular expression
 *
 * @param regex - Regular expression
 * @returns Escaped regular expression
 */
const escapeRegExp = (regex: string): string => {
    return regex.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& is the whole matched string
};

/**
 * Generates a global regular expression to do string replacements
 *
 * @param regex - Regular expression
 * @returns Generated regular expression
 */
const generateRegExpForReplacement = (regex: string): RegExp => {
    return new RegExp(escapeRegExp(regex), "g");
};

/**
 * Gets the namespace value from a namespace
 *
 * @param namespaces - Namespaces
 * @param type - Namespace type
 * @returns Namespace value
 */
const getNamespace = (
    namespaces: Map<string, string>,
    type: string = "w"
): string => {
    let namespacesValue =
        "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

    if (namespaces.has(type)) {
        const newNamespace = namespaces.get(type);
        if (newNamespace != null) {
            namespacesValue = newNamespace;
        }
    }

    return namespacesValue;
};

/**
 * Normalizes a color
 *
 * @param content - Color
 * @returns Normalized color
 */
const normalizeColor = (content: string): string => {
    return content.replace(/#/g, "");
};

/**
 * Generates a random number
 *
 * @param min - Minimum number
 * @param max - Maximum number
 * @param exclude - Exclude numbers
 */
const generateRandomNumber = (
    min: number,
    max: number,
    exclude: number[]
): number => {
    let isNewNumber = false;
    const generateNewRandomNumber = (min: number, max: number) =>
        Math.floor(Math.random() * (max - min + 1)) + min;
    let newRandomNumber = generateNewRandomNumber(min, max);

    // if the random number must be excluded, get a new random number that is not excluded
    if (exclude.includes(newRandomNumber)) {
        while (!isNewNumber) {
            newRandomNumber = generateNewRandomNumber(min, max);
            if (!exclude.includes(newRandomNumber)) {
                isNewNumber = true;
            }
        }
    }

    return newRandomNumber;
};

/**
 * Generates a unique Id
 *
 * @param prefix - Unique Id prefix
 * @returns Unique Id
 */
const generateUniqid = (prefix: string): string => {
    const token1 =
        Date.now() * 1000 + generateRandomNumber(999999, 99999999, []);

    return prefix + token1.toString(16).replace(/\./g, "").padEnd(14, "0");
};

export {
    applyHtmlSpecialChars,
    convertMeasuringUnit,
    escapeRegExp,
    generateRegExpForReplacement,
    getNamespace,
    generateRandomNumber,
    generateUniqid,
    normalizeColor,
    MeasuringUnit,
};
